import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useEmployeeLookupSelector } from '@/hooks/employee/useEmployeeLookupSelector';
import { incidentErrorTypeService, type IncidentErrorType } from '@/api/domains/catalog';
import { employeeIncidentService } from '@/api/domains/employeeIncident';
import type {
  IncidentManagementItem,
  IncidentManagementTotals,
  IncidentStatus,
} from '@/api/domains/employeeIncident';
import { formatDateToYYYYMMDD, calculateDateRange, reorderToApellidosNombreFormat } from '@/utils';
import type { TableColumn } from '@/components/ui/tables/Table.types';
import type { DateRangeValue } from '@/components/ui/filters/DateRangeFilter';


const ORDER_BY_MAP: Record<string, string> = {
  taxId: 'taxIdentificationNumber',
  employeeName: 'employeeFullName',
  errorType: 'errorType',
  incidentDate: 'incidentDate',
  markingDate: 'markingDate',
  markingTime: 'markingTime',
  locationName: 'locationName',
  status: 'status',
};

interface FilterState {
  dateFrom: string;
  dateTo: string;
  errorTypeId: number | null;
  status: IncidentStatus;
  filterEmployeeId: number | null;
}

const ZERO_TOTALS: IncidentManagementTotals = {
  pendientes: 0,
  aprobados: 0,
  rechazados: 0,
  error: 0,
  todos: 0,
};

export interface UseIncidentManagementListResult {
  rows: IncidentManagementItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<IncidentManagementItem>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  isLoading: boolean;
  errorTypeOptions: { value: string; label: string }[];
  statusOptions: { value: string; label: string }[];
  formattedFilterValues: Record<string, string>;
  handleFilterChange: (key: string, value: string | string[]) => void;
  dateFrom: string;
  dateTo: string;
  defaultDateFrom: string;
  defaultDateTo: string;
  onDateRangeChange: (range: DateRangeValue) => void;
  employeeSelector: ReturnType<typeof useEmployeeLookupSelector>;
  employeeSelectorOptions: { value: string; label: string }[];
  totals: IncidentManagementTotals;
  errorTypeId: number | null;
  status: IncidentStatus;
  filterEmployeeId: number | null;
  searchText: string;
}

export function useIncidentManagementList(): UseIncidentManagementListResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();

  const [totals, setTotals] = useState<IncidentManagementTotals>(ZERO_TOTALS);
  // Preserva el label del colaborador elegido: al seleccionar desde una búsqueda,
  // cerrar el selector limpia el texto buscado y recarga la lista por defecto,
  // que puede no incluir al colaborador ya elegido (ver ASIS-5244, feedback QA).
  const [selectedEmployeeLabel, setSelectedEmployeeLabel] = useState<string | null>(null);

  const incidentErrorTypesCatalog = useCatalogLoader<IncidentErrorType>(
    (signal) => incidentErrorTypeService.getIncidentErrorTypes(signal),
    (type) => ({ value: String(type.id), label: type.description }),
    [],
  );

  const employeeSelector = useEmployeeLookupSelector();

  const initialFilters = useMemo<FilterState>(() => {
    const defaultRange = calculateDateRange('MONTH_1', formatDateToYYYYMMDD);
    return {
      dateFrom: defaultRange.dateFrom,
      dateTo: defaultRange.dateTo,
      errorTypeId: null,
      status: 'todos',
      filterEmployeeId: null,
    };
  }, []);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<FilterState>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<IncidentManagementItem> | null> => {
      const response = await employeeIncidentService.listManagement(
        {
          dateFrom: params.filters.dateFrom,
          dateTo: params.filters.dateTo,
          errorTypeId: params.filters.errorTypeId,
          status: params.filters.status === 'todos' ? null : params.filters.status,
          filterEmployeeId: params.filters.filterEmployeeId,
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText || null,
          orderBy: params.sort?.key ?? null,
          orderDirection: params.sort?.direction ?? null,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('attendance:incidentManagement.messages.loadError'));
        setTotals(ZERO_TOTALS);
        return null;
      }

      setTotals(response.data.totals);
      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    debouncedSearchText,
  } = usePaginatedList<IncidentManagementItem, FilterState>({
    fetchFn,
    initialFilters,
    initialSort: { key: 'incidentDate', direction: 'DESC' },
    orderByMap: ORDER_BY_MAP,
  });

  const handleFilterChange = useCallback(
    (key: string, value: string | string[]) => {
      if (typeof value !== 'string') return;

      if (key === 'errorTypeId') {
        setFilters((previous) => ({ ...previous, errorTypeId: value === '0' ? null : Number(value) }));
        return;
      }

      if (key === 'status') {
        setFilters((previous) => ({ ...previous, status: value as IncidentStatus }));
        return;
      }

      if (key === 'filterEmployeeId') {
        if (value === '0') {
          setSelectedEmployeeLabel(null);
        } else {
          const found = employeeSelector.options.find((option) => option.value === value);
          setSelectedEmployeeLabel(found ? reorderToApellidosNombreFormat(found.label) : null);
        }
        setFilters((previous) => ({ ...previous, filterEmployeeId: value === '0' ? null : Number(value) }));
      }
    },
    [setFilters, employeeSelector.options],
  );

  const onDateRangeChange = useCallback(
    (range: DateRangeValue) => {
      setFilters((previous) => ({ ...previous, dateFrom: range.from ?? '', dateTo: range.to ?? '' }));
    },
    [setFilters],
  );

  const errorTypeOptions = useMemo(
    () => [
      { value: '0', label: translate('attendance:incidentManagement.filters.allErrorTypes') },
      ...incidentErrorTypesCatalog.options,
    ],
    [incidentErrorTypesCatalog.options, translate],
  );

  const statusOptions = useMemo(
    () => [
      { value: 'todos', label: translate('attendance:incidentManagement.filters.allStatuses') },
      { value: 'pendiente', label: translate('attendance:incidentManagement.status.pendiente') },
      { value: 'aprobado', label: translate('attendance:incidentManagement.status.aprobado') },
      { value: 'rechazado', label: translate('attendance:incidentManagement.status.rechazado') },
      { value: 'error', label: translate('attendance:incidentManagement.status.error') },
    ],
    [translate],
  );

  // Los tres filtros (tipo de error, estado, colaborador) se renderizan como Selector
  // individuales dentro del panel "Filtros" (ver página), en ese orden, según Figma —
  // el colaborador requiere búsqueda paginada en servidor (useEmployeeLookupSelector),
  // que el componente genérico FilterDropdown no soporta en su lista estática de opciones.
  const formattedFilterValues = useMemo(
    () => ({
      status: filters.status,
      errorTypeId: filters.errorTypeId !== null ? String(filters.errorTypeId) : '0',
      filterEmployeeId: filters.filterEmployeeId !== null ? String(filters.filterEmployeeId) : '0',
    }),
    [filters.status, filters.errorTypeId, filters.filterEmployeeId],
  );

  // Opciones del selector de colaborador con sentinel "Todos" (value '0') prepended,
  // ya que useEmployeeLookupSelector no incluye esta opción por defecto.
  const employeeSelectorOptions = useMemo(() => {
    const base = [
      { value: '0', label: translate('attendance:incidentManagement.filters.allEmployees') },
      ...employeeSelector.options.map((option) => ({
        ...option,
        label: reorderToApellidosNombreFormat(option.label),
      })),
    ];

    // Si el colaborador seleccionado ya no está en la lista (p.ej. tras cerrar el
    // selector, que limpia la búsqueda y recarga la página por defecto), lo
    // reinsertamos usando el label capturado al momento de elegirlo, para que el
    // Selector siga mostrando su nombre en vez de caer al placeholder "Todos".
    const selectedId = filters.filterEmployeeId !== null ? String(filters.filterEmployeeId) : null;
    const isMissing = selectedId && !base.some((option) => option.value === selectedId);
    if (selectedId && selectedEmployeeLabel && isMissing) {
      return [...base, { value: selectedId, label: selectedEmployeeLabel }];
    }

    return base;
  }, [employeeSelector.options, filters.filterEmployeeId, selectedEmployeeLabel, translate]);

  const columns: TableColumn<IncidentManagementItem>[] = useMemo(
    () => [
      {
        key: 'taxId',
        header: translate('attendance:incidentManagement.columns.taxId'),
        sortable: true,
      },
      {
        key: 'employeeName',
        header: translate('attendance:incidentManagement.columns.employee'),
        sortable: true,
      },
      {
        key: 'errorType',
        header: translate('attendance:incidentManagement.columns.errorType'),
        sortable: true,
      },
      {
        key: 'incidentDate',
        header: translate('attendance:incidentManagement.columns.incidentDate'),
        sortable: true,
      },
      {
        key: 'markingDate',
        header: translate('attendance:incidentManagement.columns.markingDate'),
        sortable: true,
      },
      {
        key: 'markingTime',
        header: translate('attendance:incidentManagement.columns.markingTime'),
        sortable: true,
      },
      {
        key: 'locationName',
        header: translate('attendance:incidentManagement.columns.location'),
        sortable: true,
      },
      {
        key: 'status',
        header: translate('attendance:incidentManagement.columns.status'),
        sortable: true,
      },
    ],
    [translate],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    onSearch,
    onSortChange,
    isLoading: loading,
    errorTypeOptions,
    statusOptions,
    formattedFilterValues,
    handleFilterChange,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    defaultDateFrom: initialFilters.dateFrom,
    defaultDateTo: initialFilters.dateTo,
    onDateRangeChange,
    employeeSelector,
    employeeSelectorOptions,
    totals,
    errorTypeId: filters.errorTypeId,
    status: filters.status,
    filterEmployeeId: filters.filterEmployeeId,
    searchText: debouncedSearchText ?? '',
  };
}

export default useIncidentManagementList;
